<template>
  <div class="account-infos">
    <div class="_company-image">
      <div class="_avatar">
        <img
          v-if="companyImage !== ''"
          :src="companyImage"
          :alt="`${companyName} Logo`"
        >
        <div
          v-else
          class="_img-placeholder"
        >
          <div class="_text">
            {{ companyNameField.value === '' ? 'M' : companyNameField.value.charAt(0) }}
          </div>
        </div>
        <input
          ref="fileinput"
          type="file"
          :style="{display: 'none'}"
          accept="image/*"
          @change="fileChanged"
        >
        <m-dialog
          v-model:value="showCropImage"
          :title="$t('accountSettings.setCompanyPicture')"
          hide-footer
          keep-open-on-mask-click
          no-padding
        >
          <image-crop
            v-if="file !== null"
            class="_image-crop"
            :file="file"
            @cropped="uploadImage"
            @cancel="cancelImageCrop"
            @wrong-file-type="handleWrongFileType"
          />
        </m-dialog>
      </div>
      <div class="_upload-btn">
        <m-btn
          hide-border
          small
          light
          @click="triggerInput"
        >
          {{ $t('accountInfos.uploadImage') }}
        </m-btn>
      </div>
    </div>
    <div class="_form">
      <div class="_item">
        <div class="_label">
          {{ $t('accountInfos.companyNameLabel') }}
        </div>
        <m-tooltip
          :force-show="companyNameField.hasError"
          :relocate-key="companyNameField.value"
          placement="left"
        >
          <m-text-field
            v-model:value="companyNameField.value"
            :has-error="companyNameField.hasError"
            auto-focus
            @blur="companyNameField.onBlur"
            @keydown.enter="update"
          />
          <template
            v-if="companyNameField.hasError"
            #title
          >
            {{ companyNameField.error }}
          </template>
        </m-tooltip>
      </div>
      <div class="_item">
        <div class="_label">
          {{ $t('accountInfos.companySize') }}
        </div>
        <m-select
          v-model:value="companySize"
          :items="items"
          full-width
          match-trigger-width
          :style="{ background: 'white' }"
        />
      </div>
      <m-btn
        color="black"
        :disabled="disabled"
        block
        @click="update"
      >
        {{ $t('onboarding.launchMooncamp') }}
      </m-btn>
    </div>
  </div>
</template>

<script>
import ImageCrop from '@/components/ImageCrop.vue';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useOverlaySpinner from '@/composables/overlay-spinner';
import usePersonalAppSettings from '@/composables/logged-in-user/personal-app-settings';
import { DateTime } from 'luxon';
import { copy } from 'shared/lib/copy';
import { logCatch } from '@/lib/logger/logger';
import { ref } from 'vue';
import { useCompanyNameField } from '@/composables/field-validation';

export default {
  name: 'AccountInfos',
  setup() {
    const { loggedInUser } = useLoggedInUser();
    const { personalAppSettings } = usePersonalAppSettings(loggedInUser);
    const accountSvc = useLoggedInUserAccount();
    const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner();

    const companyNameField = useCompanyNameField(ref(''));

    return {
      loggedInUser,
      personalAppSettings,
      loggedInUserAccount: accountSvc.loggedInUserAccount,
      accountSvc,
      showOverlaySpinner,
      hideOverlaySpinner,
      companyNameField,
    };
  },
  emits: ['done'],
  components: { ImageCrop },
  data() {
    return {
      file: null,
      showCropImage: false,
      companySize: null,
      items: [
        {
          text: '1-50',
          value: '1-20',
        },
        {
          text: '51-100',
          value: '21-100',
        },
        {
          text: '101-500',
          value: '101-500',
        },
        {
          text: '501-1000',
          value: '501-1000',
        },
        {
          text: '1001-5000',
          value: '1001-5000',
        },
        {
          text: '5000+',
          value: '5000+',
        },
      ],
    };
  },
  computed: {
    disabled() {
      return !this.companyNameField.isValid || this.companySize === null;
    },
    companyImage() {
      if (this.loggedInUserAccount.companyImage === null) {
        return '';
      }

      return this.loggedInUserAccount.companyImage.getURL;
    },
  },
  methods: {
    pathFormatter(value) {
      let v = value;
      v = v.toLowerCase();
      v = v.replace('ö', 'oe');
      v = v.replace('ä', 'ae');
      v = v.replace('ü', 'ue');
      v = v.replace('ß', 'ss');
      v = v.replace(/\s/g, '-');
      v = v.replace(/[^a-zA-Z0-9_-]/g, '');
      return v;
    },
    update() {
      this.companyNameField.onSubmit();
      if (this.disabled) {
        return;
      }

      const now = DateTime.local().toISO();
      this.showOverlaySpinner(this.$t('moduleSelectionInfos.creatingYourAccount'));
      const update = copy(this.loggedInUserAccount);
      update.companyName = this.companyNameField.value;
      update.activeSlug = { slug: this.pathFormatter(this.companyNameField.value) };
      update.accountSettings = {
        ...update.accountSettings,
        accountInfosCompleted: now,
        moduleSelectionCompleted: now,
        usesUpdates: true,
        usesGoals: true,
      };
      update.numberOfEmployees = this.companySize;

      if (update.appIntegration !== null && typeof update.appIntegration !== 'undefined' && update.appIntegration.slackBotAccessToken !== null) {
        delete update.appIntegration.slackBotAccessToken;
      }

      this.accountSvc.updateSingle(
        update,
        { optimistic: false },
        { forceToSetSlug: true },
      ).then(() => {
        this.$emit('done');
      }).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      })).finally(() => {
        this.hideOverlaySpinner();
      });
    },
    triggerInput() {
      this.$refs.fileinput.click();
    },
    uploadImage(blob) {
      this.accountSvc.uploadImage(blob).then(() => {
        this.file = null;
      }).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      })).finally(() => { this.showCropImage = false; });
    },
    cancelImageCrop() {
      this.showCropImage = false;
      this.file = null;
    },
    handleWrongFileType() {
      this.showCropImage = false;
      this.file = null;
      this.$showSnackbar({ color: 'error', message: this.$t('error.notAnImage') });
    },
    fileChanged(event) {
      if (event.target.files.length === 0) {
        return;
      }

      this.file = event.target.files[0];
      this.showCropImage = true;
      this.$refs.fileinput.value = '';
    },
  },
  watch: {
    showCropImage(newValue) {
      if (!newValue) {
        this.file = null;
      }
    },
  },
  created() {
    this.companyNameField.value = this.loggedInUserAccount.companyName;
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  @import "shared/assets/scss/box-shadow";

  .account-infos {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    ._company-image {
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-bottom: 2rem;

      ._avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.6rem;
        text-align: center;

        img {
          width: 6.4rem;
          height: 6.4rem;
          border-radius: .4rem;

          @include box_shadow(1);
        }

        ._img-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 6.4rem;
          height: 6.4rem;
          background-color: map_get($grey, 'darken-1');
          border-radius: .4rem;

          ._text {
            font-size: $font-size-8;
            color: white;
          }
        }
      }
    }

    ._form {
      width: 100%;
      max-width: 28rem;

      ._item {
        margin-bottom: 1.6rem;

        ._label {
          font-size: $font-size-3;
          color: $font-color-secondary;
        }
      }
    }

  }
</style>
